import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Shield, FileCheck, FileX, Clock, User, Mail, Phone,
  Eye, CheckCircle, XCircle, AlertCircle, Download, Search
} from 'lucide-react';
import axios from 'axios';

const AdminIdentityVerification = () => {
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total_verified_users: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [reviewModal, setReviewModal] = useState(false);
  const [previewModal, setPreviewModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchDocuments();
    fetchStats();
  }, [activeTab]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/identity/admin/pending?status=${activeTab}`,
        { headers: getAuthHeader() }
      );
      setDocuments(response.data.documents || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/identity/admin/stats`,
        { headers: getAuthHeader() }
      );
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleReview = async (status) => {
    if (!selectedDoc) return;
    
    try {
      await axios.put(
        `${API_URL}/api/identity/admin/${selectedDoc.id}/review`,
        { status, notes: reviewNotes },
        { headers: getAuthHeader() }
      );
      alert(`Document ${status} successfully`);
      setReviewModal(false);
      setSelectedDoc(null);
      setReviewNotes('');
      fetchDocuments();
      fetchStats();
    } catch (err) {
      alert('Failed to review document');
    }
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      nic: 'National ID Card',
      passport: 'Passport',
      driving_license: 'Driving License'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status]}>{status.toUpperCase()}</Badge>;
  };

  const filteredDocuments = documents.filter(doc =>
    doc.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.user?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6" />
          Identity Verification
        </h1>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Review</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <Clock className="h-10 w-10 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approved</p>
                <p className="text-3xl font-bold text-green-600">{stats.approved}</p>
              </div>
              <FileCheck className="h-10 w-10 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Rejected</p>
                <p className="text-3xl font-bold text-red-600">{stats.rejected}</p>
              </div>
              <FileX className="h-10 w-10 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Verified Users</p>
                <p className="text-3xl font-bold text-blue-600">{stats.total_verified_users}</p>
              </div>
              <CheckCircle className="h-10 w-10 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs and Search */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Document Queue</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="pending">Pending ({stats.pending})</TabsTrigger>
              <TabsTrigger value="approved">Approved ({stats.approved})</TabsTrigger>
              <TabsTrigger value="rejected">Rejected ({stats.rejected})</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>

            <div className="space-y-4">
              {loading ? (
                <p className="text-center py-8 text-gray-500">Loading...</p>
              ) : filteredDocuments.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No documents found</p>
              ) : (
                filteredDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="border rounded-lg p-4 flex items-center justify-between hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                        <FileCheck className="h-8 w-8 text-gray-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{doc.user?.name || 'Unknown User'}</p>
                          {getStatusBadge(doc.status)}
                        </div>
                        <p className="text-sm text-gray-500">{doc.user?.email}</p>
                        <p className="text-sm text-gray-500">
                          {getDocumentTypeLabel(doc.document_type)} • 
                          Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedDoc(doc);
                          setPreviewModal(true);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      {doc.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedDoc(doc);
                            setReviewModal(true);
                          }}
                        >
                          Review
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </Tabs>
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <Dialog open={previewModal} onOpenChange={setPreviewModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Document Preview</DialogTitle>
          </DialogHeader>
          {selectedDoc && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">User</p>
                  <p className="font-medium">{selectedDoc.user?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Document Type</p>
                  <p className="font-medium">{getDocumentTypeLabel(selectedDoc.document_type)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{selectedDoc.user?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  {getStatusBadge(selectedDoc.status)}
                </div>
              </div>
              <div className="border rounded-lg p-4 bg-gray-50">
                <img
                  src={`${API_URL}${selectedDoc.file_path}`}
                  alt="Document"
                  className="max-w-full h-auto mx-auto max-h-96 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div className="hidden text-center py-8">
                  <FileCheck className="h-16 w-16 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">Preview not available</p>
                  <a
                    href={`${API_URL}${selectedDoc.file_path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Download Document
                  </a>
                </div>
              </div>
              {selectedDoc.review_notes && (
                <div>
                  <p className="text-sm text-gray-500">Review Notes</p>
                  <p className="font-medium">{selectedDoc.review_notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Modal */}
      <Dialog open={reviewModal} onOpenChange={setReviewModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Document</DialogTitle>
          </DialogHeader>
          {selectedDoc && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium">{selectedDoc.user?.name}</p>
                <p className="text-sm text-gray-500">{selectedDoc.user?.email}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Document: {getDocumentTypeLabel(selectedDoc.document_type)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Review Notes (optional)</label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes about this review..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setReviewModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleReview('rejected')}
            >
              <XCircle className="h-4 w-4 mr-1" />
              Reject
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleReview('approved')}
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminIdentityVerification;
